import React, { useState } from 'react';
import { OrderItemDetail } from '../../../../api/shop/types/orderTypes';
import { getImageUrl } from '../../../../utils/imageUrl';
import secureApi from '../../../../utils/secureApi';
import { SHOP_ENDPOINTS } from '../../../../api/shop/endpoints';
import './OrderItems.css';

interface OrderItemsProps {
  orderItems: OrderItemDetail[];
  paymentMethod: string;
  sellerName: string;
  orderId: string;
  orderItemId: string;
  showViewPaymentInfo?: boolean;
  onViewPaymentInfo?: () => void;
}

const OrderItems: React.FC<OrderItemsProps> = ({ 
  orderItems, 
  paymentMethod, 
  sellerName, 
  orderId, 
  orderItemId,
  showViewPaymentInfo = false,
  onViewPaymentInfo
}) => {
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  const formatPrice = (price: number) => {
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const calculateTotalPrice = () => {
    return orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getPaymentMethodDisplay = (method: string) => {
    switch (method) {
      case 'netbanking':
        return 'Net Banking';
      case 'upi':
        return 'UPI';
      case 'card':
        return 'Card';
      default:
        return method.toUpperCase();
    }
  };

  const handleGetInvoice = async () => {
    try {
      setIsLoadingInvoice(true);
      // Call API to get invoice link using SHOP_ENDPOINTS
      const response = await secureApi.get(SHOP_ENDPOINTS.ORDERS.GET_INVOICE(orderId, orderItemId));
      
      // Handle different possible response structures
      const invoiceUrl = response?.invoiceUrl || 
                        response?.data?.invoiceUrl || 
                        response?.url ||
                        response?.data?.url ||
                        response?.link ||
                        response?.data?.link;
                        
      if (invoiceUrl) {
        // Open invoice in new tab
        window.open(invoiceUrl, '_blank');
      } else {
        // If no URL is returned, try to download as blob
        if (response) {
          const blob = new Blob([response], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `invoice-${orderId}-${orderItemId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          alert('Unable to download invoice. Please try again.');
        }
      }
    } catch (error) {
      alert('Error downloading invoice. Please try again.');
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  return (
    <div className="order-items-container">
      <h3 className="order-items-title">Items</h3>
      
      {orderItems.map((item, index) => {
        // Use stable key based on product name and index to prevent unnecessary re-renders
        const itemKey = `${item.productName}-${index}`;
        // Memoize image URL to prevent recalculation on every render
        const imageUrl = getImageUrl(item.image);
        
        return (
          <div key={itemKey} className="order-item">
            <div className="order-item-image">
              <img 
                src={imageUrl}
                alt={item.productName}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  // Prevent infinite loop - only set placeholder once
                  if (target.src !== '/placeholder-product.png' && !target.dataset.errorHandled) {
                    target.dataset.errorHandled = 'true';
                    target.src = '/placeholder-product.png';
                  }
                }}
                loading="lazy"
              />
            </div>
          
          <div className="order-item-details">
            <div className="order-item-name">{item.productName}</div>
            <div className="order-item-meta">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="order-item-price">{formatPrice(item.price)}</span>
                {showViewPaymentInfo && onViewPaymentInfo && (
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onViewPaymentInfo();
                    }}
                    style={{ 
                      fontSize: '14px', 
                      color: '#006EFF', 
                      textDecoration: 'none',
                      fontWeight: '400'
                    }}
                  >
                    View payment info
                  </a>
                )}
              </div>
              <span className="order-item-color">
                {item.color || ''}{item.color ? ' • ' : ''}{(item as any).size || 'UK5'}
              </span>
            </div>
          </div>
          
          <div className="order-item-price-total">
            {formatPrice(item.price * item.quantity)}
          </div>
          
          <div className="order-item-quantity">
            <button className="quantity-btn" disabled>
              <i className="ri-subtract-line"></i>
            </button>
            <span className="quantity-value">{item.quantity}</span>
            <button className="quantity-btn" disabled>
              <i className="ri-add-line"></i>
            </button>
          </div>
        </div>
        );
      })}
      
      {/* Total Price */}
      <div className="order-items-total">
        <div className="order-total-row">
          <span className="order-items-title-text">Total Item Price</span>
          <span className="order-items-value">{formatPrice(calculateTotalPrice())}</span>
        </div>
      </div>
      
      {/* Payment Method */}
      <div className="order-payment-section">
        <div className="order-payment-method">
          <div className="payment-method-icon">
            <span className="payment-method-icon-text">{getPaymentMethodDisplay(paymentMethod)}</span>
          </div>
          <div className="payment-method-text-container">
            <span className="payment-method-text">Paid by {getPaymentMethodDisplay(paymentMethod)}</span>
          </div>
        </div>
      </div>
      
      {/* Seller Info */}
      <div className="order-seller-section">
        <div className="order-seller-row">
          <div className="order-seller-info-left">
            <span className="order-seller-label">Sold by</span>
            <span className="order-seller-name">{sellerName}</span>
          </div>
          <button 
            className="get-invoice-btn" 
            onClick={handleGetInvoice} 
            disabled={isLoadingInvoice}
          >
            {isLoadingInvoice ? 'Getting...' : 'Get Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderItems;